/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    Configuration,
    ConfigurationParameters,
    BodyInfo,
    BodyInfoApi,
    BodyInfoGetRequest,
} from 'client-code/body-info-service';
import { IBodyInfoServiceRepository } from '../interfaces/IBodyInfoServiceRepository';
import { TServicesParams } from '../types/TServicesParams';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { Log } from '../logging/Log';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { Storage } from '../storage/Storage';
import { CachingPolicy } from '../cachingpolicy/CachingPolicy';
import { DeviceModel } from '../device/DeviceModel';
import { APPLICATION_FEATURE_AREA } from '../../config';

export class BodyInfoServiceRepositoryImpl implements IBodyInfoServiceRepository {
    mParams: TServicesParams;

    TAG: string = 'BodyInfoServiceRepositoryImpl';

    constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private async getBodyInfoFromService() {
        try {
            const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.STARTUP).headers;
            const configurationParameters: ConfigurationParameters = {
                apiKey: this.mParams.accessToken,
                basePath: this.mParams.basePath,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };

            const requestParameters: BodyInfoGetRequest = {
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                applicationVersion: DeviceModel.getAppVersionName(),
                bodyId: this.mParams.bodyId,
                productName: DeviceModel.getTiVoSettings().getAppName(),
                deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
                xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            };

            const configuration = new Configuration(configurationParameters);
            const bodyInfoService = new BodyInfoApi(configuration);
            const bodyInfoServiceResponse = await bodyInfoService.bodyInfoGet(requestParameters);

            Storage.getInstance(Storage.StorageType.SESSION_STORAGE).setItem(
                Storage.Keys.BODY_INFO_SERVICE_DATA,
                bodyInfoServiceResponse.data,
            );

            Storage.getInstance(Storage.StorageType.SESSION_STORAGE).setItem(
                Storage.Keys.BODY_INFO_SERVICE_LAST_CALL_TIME,
                Date.now(),
            );
        } catch (error) {
            Log.e(this.TAG, 'getBodyInfoFromService', error);
            throw error;
        }
    }

    /**
     * function to update and return BodyInfo from cache
     * @returns { BodyInfo }
     */
    async fetchAndStoreBodyInfoData(): Promise<BodyInfo> {
        try {
            const currentTime = Date.now();
            const bodyInfoLastCallTime = Storage.getInstance(
                Storage.StorageType.SESSION_STORAGE,
            ).getItem(Storage.Keys.BODY_INFO_SERVICE_LAST_CALL_TIME);
            const bodyInfoData = Storage.getInstance(Storage.StorageType.SESSION_STORAGE).getItem(
                Storage.Keys.BODY_INFO_SERVICE_DATA,
            );
            const isServiceCallRequired: boolean =
                !bodyInfoLastCallTime ||
                bodyInfoLastCallTime + CachingPolicy.BodyInfoService.cachingDuration < currentTime;
            const isDataAvailable: boolean = bodyInfoData;

            if (isServiceCallRequired || !isDataAvailable) {
                await this.getBodyInfoFromService();
            }
            return Storage.getInstance(Storage.StorageType.SESSION_STORAGE).getItem(
                Storage.Keys.BODY_INFO_SERVICE_DATA,
            );
        } catch (error) {
            Log.e(this.TAG, 'fetchAndStoreBodyInfoData', error);
            throw error;
        }
    }

    /**
     * function to get body info
     * @returns { BodyInfo }
     */
    fetchBodyInfo(): BodyInfo {
        try {
            return Storage.getInstance(Storage.StorageType.SESSION_STORAGE).getItem(
                Storage.Keys.BODY_INFO_SERVICE_DATA,
            );
        } catch (error) {
            Log.e(this.TAG, 'fetchBodyInfo', error);
            throw error;
        }
    }
}
